<script lang="ts">
    import SelectionPage from '@Quest/PortfolioScenarios/Pages/SelectionPage'
    export default SelectionPage
</script>

<template>
    <div>

        <PortfolioNavigation :workspaceId="workspaceId" :projectId="tchProjectId" />

        <div v-if="rm" class="container-fluid" style="margin-top: 80px; padding-top: 20px;">
            <div class="row">
                <div class="col-lg-6">
                    <small class="text-muted">{{rm.PortfolioName}} / {{rm.ScenarioName}}</small>
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Plan: Selection</h1>
                </div>
                <div class="col-lg-6 d-flex flex-column justify-content-end">
                    <PortfolioScenarioNavigation :portfolioId="rm.PortfolioId"
                                                 :corporateScenarioUrl="rm.CorporateScenarioUrl"
                                                 @selector-opened="handleUpdateScenarioAcceptancesCommand"
                                                 @settings-opened="handleUpdateScenarioAcceptancesCommand" />
                </div>
            </div>

            <hr />

            <div class="row mt-5">
                <div class="col-sm-6 col-md-4 col-lg-3 col-xl-3" v-for="m in rm.Metrics" :key="m.Id">
                    <div class="card mt-2">
                        <div class="card-body text-center">
                            <h5 class="card-title">
                                {{m.Title}}
                            </h5>
                            <h6 class="card-subtitle mb-2 text-muted">{{m.Subtitle}}</h6>
                            <span class="h1">{{m.PlannedValueString ?? '&nbsp'}}</span>
                            <br />
                            <span class="h5 text-muted">{{m.PlannedPercentageString ?? '&nbsp' }}</span>

                            <div class="text-left">
                                <hr class="my-3" />
                                <h6>All: {{m.RequestedValueString}} <small class="text-muted" v-if="m.RequestedPercentageString">({{m.RequestedPercentageString}})</small></h6>
                                <h6>Accepted: {{m.AcceptedValueString ?? 'NA'}} <small class="text-muted" v-if="m.AcceptedPercentageString">({{m.AcceptedPercentageString}})</small></h6>
                                <h6>Rejected: {{m.RejectedValueString ?? 'NA'}} <small class="text-muted" v-if="m.RejectedPercentageString">({{m.RejectedPercentageString}})</small></h6>
                                <h6>TBD: {{m.TbdValueString ?? 'NA'}} <small class="text-muted" v-if="m.TbdPercentageString">({{m.TbdPercentageString}})</small></h6>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="card mt-3">
                <div class="card-header pointer" @click="resourcesSectionExpanded = !resourcesSectionExpanded">
                    <h5>Resources</h5>
                </div>
                <div class="card-body" v-if="resourcesSectionExpanded">
                    <div class="row">
                        <div class="col-sm-6 col-md-4 col-lg-3 col-xl-3" v-for="r in rm.ResourceConstraints" :key="r.Id">
                            <div class="card mt-1" :class="getResourceCardClass(r)">
                                <div class="card-body text-center">
                                    <h5 class="card-title">
                                        <!--{{r.Name}} <small>{{r.Unit}}</small>-->
                                        <a class="text-dark" v-sanitizedHref="`/#/portfolio-plan/resource/${workspaceId}/${tchProjectId}/${scenarioId}/${r.Id}`">
                                            {{r.Name}} <small>{{r.Unit}}</small>
                                        </a>
                                    </h5>
                                    <h6 class="card-subtitle mb-2 text-muted">
                                        <span v-if="r.Limit != null">
                                            {{fmtPct(r.Allocation/r.Limit)}}
                                        </span><span v-else>-</span>
                                    </h6>
                                    <span class="h1">
                                        {{fmtNum(r.Allocation)}}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>


            <div class="card mt-3">
                <div class="card-header pointer" @click="constraintsSectionExpanded = !constraintsSectionExpanded">
                    <h5>Costs ({{rm.CurrencyLabel}})</h5>
                </div>
                <div class="card-body" v-if="constraintsSectionExpanded">
                    <div class="row">
                        <div class="col-sm-6 col-md-4 col-lg-3 col-xl-3" v-for="c in rm.CostConstraints" :key="c.Id">
                            <div class="card mt-1" :class="getCostCardClass(c)">
                                <div class="card-body text-center">
                                    <h5 class="card-title">
                                        {{c.Name}}
                                    </h5>
                                    <h6 class="card-subtitle mb-2 text-muted">
                                        <span v-if="c.Limit != null">
                                            {{fmtPct(c.Allocation/c.Limit)}}
                                        </span><span v-else>-</span>
                                    </h6>
                                    <span class="h1">
                                        {{fmtNum(c.Allocation)}}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="card mt-3">
                <div class="card-header pointer" @click="criteriaSectionExpanded = !criteriaSectionExpanded">
                    <h5>Criteria</h5>
                </div>
                <div class="card-body" v-if="criteriaSectionExpanded">
                    <div class="row">
                        <div class="col-sm-6 col-md-4 col-lg-3 col-xl-2" v-for="c in topCriteria" :key="c.Id">
                            <div class="card">
                                <div class="card-body text-center">
                                    <h5 class="card-title">
                                        {{c.Name}}
                                    </h5>
                                    <h6 class="card-subtitle mb-2 text-muted">Value points</h6>
                                    <span class="h1">{{c.TotalWeightedValueString ?? '&nbsp'}}</span>
                                    <span>
                                        <br /><span class="h5 text-muted">{{c.BalanceRatioString}}</span>
                                    </span>
                                    <div class="text-left">
                                        <hr class="my-3" />
                                        <h6>Overall value: {{rm.TotalAcceptedValueString}} <small class="text-muted">(100%)</small></h6>
                                        <h6>Criterion value: {{c.TotalWeightedValueString}} <small class="text-muted">({{c.ValueShareString}})</small></h6>
                                        <h6>Balanced value: {{c.BalancedWeightedValueString}} <small class="text-muted">({{c.GlobalWeightString}})</small></h6>
                                        <h6>Balance ratio: {{c.BalanceRatioString}}</h6>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row mt-3">
                <div class="col-lg-6 col-md-12">
                    <div class="card">
                        <div class="card-header">
                            <div class="float-right">
                                <div class="dropdown d-inline mr-2">
                                    <button data-toggle="dropdown" class="btn btn-secondary btn-sm">Legend</button>
                                    <div class="dropdown-menu dropdown-menu-right" role="menu">
                                        <span class="dropdown-item-text">
                                            <i class="fa-solid fa-xmark text-danger" aria-hidden="true"></i> Rejected
                                        </span>
                                        <span class="dropdown-item-text">
                                            <i class="fa-solid fa-play fa-rotate-270 text-warning" aria-hidden="true"></i> TBD
                                        </span>
                                        <span class="dropdown-item-text">
                                            <i class="fa-solid fa-circle text-success" aria-hidden="true"></i> Accepted
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <h5>Efficient Frontier</h5>
                        </div>
                        <div class="card-body chart-container">
                            <div id="Chart-EfficientFrontier" style="max-width: 100%; height: 100%; overflow: auto;"></div>
                        </div>
                    </div>
                </div>
                <div class="col-lg-6 col-md-12">
                    <div class="card">
                        <div class="card-header">
                            <div class="float-right">
                                <div class="dropdown d-inline mr-2">
                                    <button data-toggle="dropdown" class="btn btn-secondary btn-sm">Legend</button>
                                    <div class="dropdown-menu dropdown-menu-right" role="menu">
                                        <span class="dropdown-item-text">
                                            <i class="fa-solid fa-xmark text-danger" aria-hidden="true"></i> Rejected
                                        </span>
                                        <span class="dropdown-item-text">
                                            <i class="fa-solid fa-play fa-rotate-270 text-warning" aria-hidden="true"></i> TBD
                                        </span>
                                        <span class="dropdown-item-text">
                                            <i class="fa-solid fa-circle text-success" aria-hidden="true"></i> Accepted
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <h5>Prioritization Matrix</h5>
                        </div>
                        <div class="card-body chart-container">
                            <div id="Chart-PrioritizationMatrix" style="max-width: 100%; height: 100%; overflow: auto;"></div>
                        </div>
                    </div>
                </div>

            </div>

            <br /><br />

            <ProjectsSelectionView :propProjects="filteredProjects"
                                   :propCurrencyLabel="rm.CurrencyLabel"
                                   :propAllCriteria="rm.Criteria"
                                   :propComparableScenarios="rm.ComparableScenarios"
                                   :propSearchPhrase="searchPhrase"
                                   :propProjectsFilter="projectsFilter"
                                   @accept-selection="handleAcceptSelection($event)"
                                   @reject-selection="handleRejectSelection($event)"
                                   @lock-selection="handleLockSelection($event)"
                                   @unlock-selection="handleUnlockSelection($event)"
                                   @update-search-phrase="searchPhrase = $event"
                                   @update-projects-filter="projectsFilter = $event" />

        </div>

        <br />
        <br />
        <br />
        <br />

    </div>

</template>

<style scoped>
    .toolbar {
        background-color: #f8f8f8; /* Light gray background for the toolbar */
        padding: 10px 15px; /* Padding around the toolbar */
        border: 1px solid #e4e4e4; /* A light border to give separation */
        display: flex; /* Use flex to give layout flexibility */
        align-items: center; /* Center-align items vertically */
        justify-content: space-between; /* Separate items */
    }

    .over-limit {
        background-color: #dc3545;
        color: white;
    }

        .over-limit .card-subtitle {
            color: white !important;
        }

    .near-limit {
        background-color: #ffc107;
    }

        .near-limit .card-subtitle {
            color: #212529 !important; 
        }

</style>
